import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  Match,
  onMount,
  Show,
  Switch,
} from 'solid-js'
import {
  MinusCircle,
  Plus,
  Save,
  X,
  Import,
  Download,
  HelpCircle,
  ArrowLeft,
  Trash,
  ArrowRight,
  WandSparkles,
  SlidersVertical,
  Dices,
  BookPlus,
} from 'lucide-solid'
import Button from '../../shared/Button'
import PageHeader from '../../shared/PageHeader'
import TextInput, { ButtonInput } from '../../shared/TextInput'
import { FormLabel } from '../../shared/FormLabel'
import FileInput, { FileInputResult } from '../../shared/FileInput'
import {
  characterStore,
  tagStore,
  toastStore,
  memoryStore,
  chatStore,
  userStore,
} from '../../store'
import { useNavigate, useSearchParams } from '@solidjs/router'
import PersonaAttributes from '../../shared/PersonaAttributes'
import AvatarIcon from '../../shared/AvatarIcon'
import Select, { Option } from '../../shared/Select'
import TagInput from '../../shared/TagInput'
import { CultureCodes } from '../../shared/CultureCodes'
import VoicePicker from './components/VoicePicker'
import { AppSchema } from '../../../common/types/schema'
import Loading from '/web/shared/Loading'
import { JSX, For } from 'solid-js'
import { BUNDLED_CHARACTER_BOOK_ID, emptyBookWithEmptyEntry } from '/common/memory'
import { Card, Pill, SolidCard, TitleCard } from '../../shared/Card'
import { usePane, useRootModal } from '../../shared/hooks'
import Modal, { RootModal } from '/web/shared/Modal'
import EditMemoryForm, { EntrySort, getBookUpdate } from '../Memory/EditMemory'
import { Toggle, ToggleButtons } from '../../shared/Toggle'
import AvatarBuilder from '../../shared/Avatar/Builder'
import { FullSprite } from '/common/types/sprite'
import { getRandomBody } from '../../asset/sprite'
import AvatarContainer from '../../shared/Avatar/Container'
import { CharEditor, useCharEditor } from './editor'
import { downloadCharacterHub, jsonToCharacter } from './port'
import { DownloadModal } from './DownloadModal'
import ImportCharacterModal from './ImportCharacter'
import Tabs, { useTabs } from '/web/shared/Tabs'
import RangeInput from '/web/shared/RangeInput'
import { rootModalStore } from '/web/store/root-modal'
import { getAssetUrl, random } from '/web/shared/util'
import { ImageSettings } from '../Settings/Image/ImageSettings'
import { v4 } from 'uuid'
import { imageApi } from '/web/store/data/image'
import { Page } from '/web/Layout'
import { ModeGenSettings } from '/web/shared/Mode/ModeGenSettings'
import { charsApi } from '/web/store/data/chars'
import Tooltip from '/web/shared/Tooltip'
import { CharacterSchema } from './CharacterSchema'
import { UploadFile, fileUploader } from '@solid-primitives/upload'
import {
  createModelGalleryImage,
  getModelGallery,
  removeModelGalleryImage,
} from '/web/store/data/gallery'
import { ModelGalleryImage } from '/common/types/aifans-schema'

const formatOptions = [
  { value: 'attributes', label: 'Attributes (Key: value)' },
  { value: 'text', label: 'Plain Text' },
]

const categoryOptions = [
  { value: 'popular', label: 'Popular' },
  { value: 'creator', label: 'Creator Spotlight' },
  { value: 'trending', label: 'Trending Now' },
  { value: 'online', label: 'Online Creators' },
  { value: 'new', label: 'New' },
  { value: 'hentai', label: 'Hentai Creators' },
]

const backupFormats: any = {
  sbf: { value: 'sbf', label: 'SBF' },
  wpp: { value: 'wpp', label: 'W++' },
  boostyle: { value: 'boostyle', label: 'Boostyle' },
}

export const CreateCharacterForm: Component<{
  chat?: AppSchema.Chat
  editId?: string
  duplicateId?: string
  import?: string
  children?: JSX.Element
  temp?: boolean
  noTitle?: boolean
  footer?: (children: JSX.Element) => void
  close?: () => void
  onSuccess?: (char: AppSchema.Character) => void
}> = (props) => {
  let personaRef: any
  const [search, setSearch] = useSearchParams()
  const nav = useNavigate()
  const user = userStore()

  const isPage = props.close === undefined

  const paneOrPopup = usePane()
  const cancel = () => {
    if (isPage) {
      nav('/character/list')
    } else {
      props.close?.()
    }
  }
  const query = { import: props.import }
  const [forceNew, setForceNew] = createSignal<boolean>(false)

  const srcId = createMemo(() => props.editId || props.duplicateId || '')
  const [image, setImage] = createSignal<string | undefined>()
  const [controlNetImage, setControlNetImage] = createSignal<string | undefined>()
  const [openPreset, setOpenPreset] = createSignal(false)
  const [presetFooter, setPresetFooter] = createSignal<JSX.Element>()
  const [files, setFiles] = createSignal<UploadFile[]>([])
  const [modelGalleryImagesRemaining, setModelGalleryImagesRemaining] = createSignal(0)
  const [modelGalleryImages, setModelGalleryImages] = createSignal<ModelGalleryImage[]>([])

  const editor = useCharEditor()

  const tagState = tagStore()
  const state = characterStore((s) => {
    const edit = s.editing

    return {
      status: s.hordeStatus,
      avatar: s.generate,
      creating: s.creating,
      edit: forceNew() ? undefined : edit,
      list: s.characters.list,
      loaded: s.characters.loaded,
    }
  })

  const [imgUrl, setImageUrl] = createSignal<string>()

  const [tokens, setTokens] = createSignal({
    name: 0,
    scenario: 0,
    greeting: 0,
    persona: 0,
    sample: 0,
  })

  const [showBuilder, setShowBuilder] = createSignal(false)
  const [converted, setConverted] = createSignal<AppSchema.Character>()
  const [showImport, setImport] = createSignal(false)

  const personaFormats = createMemo(() => {
    const options = formatOptions.slice()
    if (editor.state.personaKind in backupFormats) {
      options.push(backupFormats[editor.state.personaKind])
    }
    return options
  })

  const totalTokens = createMemo(() => {
    const t = tokens()
    return t.name + t.persona + t.sample + t.scenario
  })

  const totalPermanentTokens = createMemo(() => {
    const t = tokens()
    return t.name + t.persona + t.scenario
  })

  const [vipFeaturedImage, setVipFeaturedImage] = createSignal<string | undefined>()

  onMount(async () => {
    characterStore.clearGeneratedAvatar()
    characterStore.clearCharacter()

    if (srcId()) {
      characterStore.getCharacter(srcId(), props.chat)

      loadModelGallery()
    }

    /* Character importing from CharacterHub */
    if (!query.import) return
    try {
      const { file, json } = await downloadCharacterHub(query.import)
      const imageData = await imageApi.getImageData(file)
      const char = jsonToCharacter(json)
      editor.load(char)
      editor.update({
        book: json.characterBook,
        alternateGreetings: json.alternateGreetings || [],
        avatar: file,
        personaKind: 'text',
      })
      editor.receiveAvatar(file)

      setImage(imageData)
      toastStore.success(`Successfully downloaded from Character Hub`)
    } catch (ex: any) {
      toastStore.error(`Character Hub download failed: ${ex.message}`)
    }
  })

  createEffect(async () => {
    if (!personaRef) return

    // We know we're waiting for a character to edit, so let's just wait
    if (!state.edit && srcId()) return

    // If this is our first pass: load something no matter what
    if (!editor.original()) {
      if (!srcId()) {
        // editor.reset(ref)
        return
      }

      // We have a `srcId`, we need to wait to receive the character we're editing
      if (!state.edit) return

      editor.load(state.edit)
      setImage(state.edit?.avatar)
      return
    }

    // This is a subsequent pass - we already have state
    // We want to avoid unnecessarily clearing/reseting state due to a websocket reconnect

    if (!state.edit) return
    if (editor.state.editId !== state.edit._id && state.edit._id === srcId()) {
      editor.update('editId', srcId())
      editor.load(state.edit)
      setImage(state.edit?.avatar)

      if (state.edit?.controlNetImage) {
        const base64 = await imageApi.getImageData(state.edit?.controlNetImage)
        setControlNetImage(base64)
      }

      if (state.edit?.vipFeaturedImage) {
        const base64 = await imageApi.getImageData(state.edit?.vipFeaturedImage)
        setVipFeaturedImage(base64)
      }

      return
    }
  })

  createEffect(() => {
    tagStore.updateTags(state.list)
    props.footer?.(footer)
  })

  const updateFile = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('avatar', undefined)
      setImage(state.edit?.avatar)
      return
    }

    const file = files[0].file
    const data = await editor.receiveAvatar(file)
    setImage(data)
  }

  const updateControlNetImage = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('controlNetImage', undefined)
      setControlNetImage(state.edit?.controlNetImage)
      return
    }

    const file = files[0].file

    if (file) {
      const base64 = await imageApi.getImageData(file)
      editor.update('controlNetImage', base64)
      setControlNetImage(base64)
    }
  }

  const updateVipFeaturedImage = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('vipFeaturedImage', undefined)
      setVipFeaturedImage(state.edit?.vipFeaturedImage)
      return
    }

    const file = files[0].file

    if (file) {
      const base64 = await imageApi.getImageData(file)
      editor.update('vipFeaturedImage', base64)
      setVipFeaturedImage(base64)
    }
  }

  const loadModelGallery = async () => {
    const res = await getModelGallery(srcId(), 1, 1000)
    if (res?.result?.images) {
      setModelGalleryImages(res?.result?.images)
    }
  }

  const uploadModelGalleryImages = async (files: UploadFile[]) => {
    setModelGalleryImagesRemaining(files.length)
    for (let x = 0; x < files.length; x++) {
      const f = files[x]
      const base64 = await imageApi.getImageData(f.file)
      if (base64 && props.editId) {
        const filename = await createModelGalleryImage(base64, srcId())
        await loadModelGallery()
      }
      setModelGalleryImagesRemaining(files.length - (x + 1))
    }
    toastStore.success(`Model gallery images have been added`)
  }

  const onSubmit = async (ev: Event) => {
    const payload = editor.payload(true)

    if (props.temp && props.chat) {
      if (editor.state.avatar) {
        const data = await imageApi.getImageData(editor.state.avatar)
        payload.avatar = data
      }
      chatStore.upsertTempCharacter(
        props.chat._id,
        { ...payload, _id: props.editId },
        (result: any) => {
          props.onSuccess?.(result)
          if (paneOrPopup() === 'popup') props.close?.()
        }
      )
    } else if (!forceNew() && props.editId) {
      characterStore.editFullCharacter(props.editId, payload, () => {
        if (isPage) {
          nav(`/character/${props.editId}/chats`)
        } else if (paneOrPopup() === 'popup') {
          props.close?.()
        }
      })
    } else {
      characterStore.createCharacter(payload, (result) => {
        setForceNew(false)
        if (isPage) nav(`/character/${result._id}/chats`)
      })
    }
  }

  const onPublish = async () => {
    const char = editor.payload(false)
    const image = editor.state.avatar ? await imageApi.getImageData(editor.state.avatar) : undefined
    charsApi.publishCharacter(char, image, (response) => {})
  }

  const footer = (
    <>
      <Button onClick={cancel} schema="secondary">
        <X />
        {props.close ? 'Close' : 'Cancel'}
      </Button>
      <Button onClick={onSubmit} disabled={state.creating}>
        <Save />
        {props.editId && !forceNew() ? 'Update' : 'Create'}
      </Button>
      <Show when={user.user?.admin}>
        <Button onClick={onPublish}>
          <BookPlus /> Publish
        </Button>
      </Show>
    </>
  )

  const showWarning = createMemo(
    () => !!props.chat?.overrides && props.chat.characterId === props.editId
  )

  const tabs = useTabs(
    ['Persona', 'Voice', 'Model Info', 'Gallery', 'Images', 'Advanced'],
    +(search.char_tab || '0')
  )

  let spriteRef: any

  return (
    <Page>
      <Show when={!props.noTitle && (isPage || paneOrPopup() === 'pane')}>
        <PageHeader
          title={`${
            forceNew() ? 'Create' : props.editId ? 'Edit' : props.duplicateId ? 'Copy' : 'Create'
          } a Character`}
          subtitle={
            <div class="whitespace-normal">
              <em>
                {totalTokens()} tokens, {totalPermanentTokens()} permanent
              </em>
            </div>
          }
        />
      </Show>
      <form
        class="relative text-base"
        onSubmit={onSubmit}
        id="character-form"
        ref={(form) => {
          personaRef = form
          editor.prepare(form)
        }}
      >
        <div class="flex flex-col gap-4">
          <Show when={!isPage}>
            <div> {props.children} </div>
          </Show>

          <Show when={showWarning()}>
            <SolidCard bg="orange-600">
              <b>Warning!</b> Your chat currently overrides your character definitions. These
              changes won't affect your current chat until you disable them in the "Edit Chat" menu.
            </SolidCard>
          </Show>

          <div class={`flex grow flex-col justify-between gap-2 pl-2 pr-3 `}>
            <Show when={!isPage && paneOrPopup() === 'popup'}>
              <div>
                <em>
                  ({totalTokens()} tokens, {totalPermanentTokens()} permanent)
                </em>
              </div>
            </Show>

            <Show when={props.temp}>
              <TitleCard type="premium">
                You are {props.editId ? 'editing' : 'creating'} a temporary character. A temporary
                character exist within your current chat only.
              </TitleCard>
            </Show>

            <div class="flex justify-end gap-2 text-[1em]">
              <Button onClick={() => setOpenPreset(true)}>
                <SlidersVertical size={24} /> Preset
              </Button>
              <Button onClick={() => setImport(true)}>
                <Import /> Import
              </Button>

              <Button onClick={() => setConverted(editor.convert())}>
                <Download /> Export
              </Button>

              <Show when={state.edit}>
                <Button
                  onClick={() => {
                    setForceNew(true)
                    editor.clear()
                  }}
                >
                  <Plus />
                  New
                </Button>
              </Show>
            </div>

            <Tabs
              select={(id) => {
                tabs.select(id)
                setSearch({ char_tab: id })
              }}
              selected={tabs.selected}
              tabs={tabs.tabs}
            />

            <div class="flex flex-col gap-2" classList={{ hidden: tabs.current() !== 'Persona' }}>
              <Button
                size="sm"
                class="w-fit"
                onClick={() => {
                  rootModalStore.info(
                    'AI Character Generation',
                    <>
                      <p class="inline-flex gap-1">
                        1. Fill out <Pill small>Name</Pill> and <Pill small>Description</Pill>{' '}
                        fields
                      </p>
                      <p class="inline-flex gap-1">
                        2. <i>(Optional)</i> Choose or update your preset{' '}
                        <Pill inverse type="hl" small>
                          <SlidersVertical size={14} />
                        </Pill>
                      </p>
                      <p class="inline-flex gap-1">
                        3. Click{' '}
                        <Pill inverse type="hl" small>
                          <WandSparkles size={14} />
                        </Pill>
                        &nbsp;on the field you'd like to generate.
                      </p>

                      <p class="inline-flex gap-1">
                        4. Adjust the <Pill small>Description</Pill> and click{' '}
                        <Pill inverse type="hl" small>
                          <WandSparkles size={14} />
                        </Pill>
                        &nbsp;to regenerate.
                      </p>
                    </>
                  )
                }}
              >
                <HelpCircle size={16} /> AI Character Generation
              </Button>
              <Card>
                <ButtonInput
                  fieldName="name"
                  required
                  label="Character Name"
                  placeholder=""
                  value={editor.state.name}
                >
                  <Button
                    size="sm"
                    schema="input"
                    onClick={() => random('first', {}).then((name) => editor.update('name', name))}
                  >
                    <Dices size={12} />
                  </Button>
                </ButtonInput>
              </Card>

              <Card>
                <TagInput
                  availableTags={tagState.tags.map((t) => t.tag)}
                  value={editor.state.tags}
                  fieldName="_tags"
                  label="Tags"
                  helperText="Used to help you organize and filter your characters."
                  onSelect={(tags) => editor.update({ tags })}
                />
              </Card>

              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Description / Creator's notes"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        A description, label, or notes for your character. This is will not
                        influence your character in any way.
                      </span>
                    </div>
                  }
                />

                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    isMultiline
                    fieldName="description"
                    parentClass="w-full"
                    value={editor.state.description}
                  />
                </div>
              </Card>

              <Card class="flex w-full flex-col gap-4 sm:flex-row">
                <div class="flex flex-col items-center gap-1">
                  <Switch>
                    <Match when={editor.state.visualType === 'sprite'}>
                      <div class="flex h-24 w-full justify-center sm:w-24" ref={spriteRef}>
                        <AvatarContainer body={editor.state.sprite} container={spriteRef} />
                      </div>
                    </Match>
                    <Match when={!state.avatar.loading}>
                      <div class="flex flex-col items-center gap-1">
                        <div
                          class="flex items-baseline"
                          style={{ cursor: state.avatar.image || image() ? 'pointer' : 'unset' }}
                          onClick={() => setImageUrl(editor.avatar() || image())}
                        >
                          <AvatarIcon
                            format={{ corners: 'sm', size: '3xl' }}
                            avatarUrl={editor.avatar() || image()}
                          />
                        </div>
                      </div>
                    </Match>
                    <Match when={state.avatar.loading}>
                      <div class="flex w-[80px] flex-col items-center justify-center">
                        <Loading type="windmill" />
                        <Show when={state.status && state.status.wait_time > 0}>
                          <span class="text-500 text-xs italic">{state.status?.wait_time}s</span>
                        </Show>
                      </div>
                    </Match>
                  </Switch>
                  <ReelControl editor={editor} loading={state.avatar.loading} />
                </div>
                <div class="flex w-full flex-col gap-2">
                  <ToggleButtons
                    items={[
                      { value: 'avatar', label: 'Avatar' },
                      { value: 'sprite', label: 'Sprite' },
                    ]}
                    onChange={(opt) => editor.update('visualType', opt.value)}
                    selected={editor.state.visualType}
                  />

                  <Switch>
                    <Match when={editor.state.visualType === 'avatar'}>
                      <FileInput
                        class="w-full"
                        fieldName="avatar"
                        label={
                          <div class="flex gap-2">
                            <div>Avatar</div>
                          </div>
                        }
                        accept="image/png,image/jpeg,image/apng,image/gif,image/webp"
                        onUpdate={updateFile}
                      />
                      <div class="flex w-full flex-col gap-2 sm:flex-row">
                        <TextInput
                          isMultiline
                          parentClass="w-full"
                          fieldName="appearance"
                          label={
                            <>
                              <Regenerate
                                field={'appearance'}
                                editor={editor}
                                allowed={editor.canGuidance}
                              />
                            </>
                          }
                          helperText={`Leave the prompt empty to use your character's persona "looks" / "appearance" attributes`}
                          placeholder="Appearance Prompt (used for Avatar Generation)"
                          value={editor.state.appearance}
                        />
                      </div>
                    </Match>
                    <Match when={true}>
                      <Button class="w-fit" onClick={() => setShowBuilder(true)}>
                        Open Character Builder
                      </Button>
                    </Match>
                  </Switch>
                  <div></div>
                </div>
              </Card>

              <Card>
                <TextInput
                  fieldName="scenario"
                  label={
                    <>
                      Scenario{' '}
                      <Regenerate field={'scenario'} editor={editor} allowed={editor.canGuidance} />
                    </>
                  }
                  helperText="The current circumstances and context of the conversation and the characters."
                  placeholder="E.g. {{char}} is in their office working. {{user}} opens the door and walks in."
                  value={editor.state.scenario}
                  isMultiline
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, scenario: v }))}
                />
              </Card>

              <Card class="flex flex-col gap-3">
                <div>
                  <FormLabel
                    label={
                      <div class="flex items-center gap-1">
                        Personality{' '}
                        <Show when={editor.state.personaKind === 'text'}>
                          <Regenerate
                            field={'persona'}
                            editor={editor}
                            allowed={editor.canGuidance}
                          />{' '}
                        </Show>
                      </div>
                    }
                    helperText={
                      <>
                        <p>If you do not know what this mean, you can leave this as-is.</p>
                        <p class="font-bold">
                          WARNING: "Plain Text" and "Non-Plain Text" schemas are not compatible.
                          Changing between them will cause data loss.
                        </p>
                        <p>Format to use for the character's format</p>
                      </>
                    }
                  />
                  <Select
                    fieldName="kind"
                    items={personaFormats()}
                    value={editor.state.personaKind}
                  />
                </div>

                <PersonaAttributes
                  value={editor.state.persona.attributes}
                  schema={editor.state.personaKind}
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, persona: v }))}
                  form={personaRef}
                  editor={editor}
                />
              </Card>
              <Card class="flex flex-col gap-3">
                <TextInput
                  isMultiline
                  fieldName="greeting"
                  label={
                    <>
                      Greeting{' '}
                      <Regenerate field={'greeting'} editor={editor} allowed={editor.canGuidance} />
                    </>
                  }
                  helperText="The first message from your character. It is recommended to provide a lengthy first message to encourage the character to give longer responses."
                  placeholder={
                    "E.g. *I smile as you walk into the room* Hello, {{user}}! I can't believe it's lunch time already! Where are we going?"
                  }
                  value={editor.state.greeting}
                  class="h-60"
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, greeting: v }))}
                />
                <AlternateGreetingsInput
                  greetings={editor.state.alternateGreetings}
                  setGreetings={(next) => editor.update({ alternateGreetings: next })}
                />
              </Card>
              <Card>
                <TextInput
                  isMultiline
                  fieldName="sampleChat"
                  label={
                    <>
                      Sample Conversation{' '}
                      <Regenerate
                        field={'sampleChat'}
                        editor={editor}
                        allowed={editor.canGuidance}
                      />
                    </>
                  }
                  helperText={
                    <span>
                      Example chat between you and the character. This section is very important for
                      teaching your character should speak.
                    </span>
                  }
                  placeholder="{{char}}: *smiles and waves back* Hello! I'm so happy you're here!"
                  value={editor.state.sampleChat}
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, sample: v }))}
                />
              </Card>
            </div>

            <div class="flex flex-col gap-2" classList={{ hidden: tabs.current() !== 'Voice' }}>
              <Card class="flex flex-col gap-3">
                <h4 class="text-md font-bold">Voice</h4>
                <Toggle
                  fieldName="voiceDisabled"
                  value={editor.state.voiceDisabled}
                  label="Disable Character's Voice"
                  helperText="Toggle on to disable this character from automatically speaking"
                />

                <VoicePicker
                  value={editor.state.voice}
                  culture={editor.state.culture}
                  onChange={(voice) => editor.update('voice', voice)}
                />

                <Select
                  fieldName="culture"
                  label="Language"
                  helperText={`The language this character speaks and understands.${
                    editor.state.culture.startsWith('en') ?? true
                      ? ''
                      : ' NOTE: You need to also translate the preset gaslight to use a non-english language.'
                  }`}
                  value={editor.state.culture}
                  items={CultureCodes}
                  onChange={(option) => editor.update('culture', option.value)}
                />
              </Card>
            </div>

            <div
              class={`flex flex-col gap-2`}
              classList={{ hidden: tabs.current() !== 'Advanced' }}
            >
              <AdvancedOptions editor={editor} />
            </div>

            <div class={`flex flex-col gap-2`} classList={{ hidden: tabs.current() !== 'Images' }}>
              <ImageSettings cfg={editor.state.imageSettings} inherit />
            </div>

            <div
              class={`flex flex-col gap-2`}
              classList={{ hidden: tabs.current() !== 'Model Info' }}
            >
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Real Name"
                  helperText={
                    <div class="flex flex-col">
                      <span>Model's real name shown on the home page and model page</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="realName"
                    parentClass="w-full"
                    value={editor.state.realName}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="URL Slug"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        The URL-friendly version of the name (e.g. 'jane-doe'). Used in the model's
                        page URL.
                      </span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="slug"
                    parentClass="w-full"
                    value={editor.state.slug}
                    placeholder="e.g. jane-doe"
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Category Slug"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        The URL-friendly version of the category (e.g. 'popular', 'trending'). Used
                        in category page URLs.
                      </span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="categorySlug"
                    parentClass="w-full"
                    value={editor.state.categorySlug}
                    placeholder="e.g. popular"
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="metaDescription"
                  value={editor.state.metaDescription}
                  label="Meta Description"
                  helperText="Description used for SEO meta tags"
                  placeholder="Enter a description for search engines"
                />
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="controlNetImage"
                  class="hidden"
                  value={controlNetImage() || editor.state.controlNetImage}
                  label="ControlNet Image"
                  helperText="(Optional) Image used in ControlNet for face consistency"
                  placeholder={``}
                />

                <Show when={controlNetImage()}>
                  <div class="relative mx-5 flex h-12 w-12 flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:shrink-0 md:grow-0 md:px-8 md:py-8">
                    <img class="absolute h-full w-full object-cover" src={controlNetImage()} />
                  </div>
                </Show>

                <FileInput
                  class="w-full"
                  fieldName="controlNetImage_file"
                  label="ControlNet Image"
                  accept="image/png,image/jpeg,image/apng"
                  onUpdate={updateControlNetImage}
                />
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="vipFeaturedImage"
                  class="hidden"
                  value={vipFeaturedImage() || editor.state.vipFeaturedImage}
                  label="VIP Featured Image"
                  helperText="(Optional) Featured image shown on VIP sign up modal"
                  placeholder={``}
                />

                <Show when={vipFeaturedImage()}>
                  <div class="relative mx-5 flex h-12 w-12 flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:shrink-0 md:grow-0 md:px-8 md:py-8">
                    <img class="absolute h-full w-full object-cover" src={vipFeaturedImage()} />
                  </div>
                </Show>

                <FileInput
                  class="w-full"
                  fieldName="vipFeaturedImage_file"
                  label="VIP Featured Image"
                  accept="image/png,image/jpeg,image/apng"
                  onUpdate={updateVipFeaturedImage}
                />
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Category"
                  helperText={
                    <div class="flex flex-col">
                      <span>This determines where the model is shown on the home page</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <Select
                    fieldName="category"
                    items={categoryOptions}
                    value={editor.state.category}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Personality"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Outgoing, Seductive</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="personality"
                    parentClass="w-full"
                    value={editor.state.personality}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Occupation"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Influencer</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="occupation"
                    parentClass="w-full"
                    value={editor.state.occupation}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Hobbies"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Photography, Fitness</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="hobbies"
                    parentClass="w-full"
                    value={editor.state.hobbies}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Relationship"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Single</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="relationship"
                    parentClass="w-full"
                    value={editor.state.relationship}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Body"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Fit</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="bodyType"
                    parentClass="w-full"
                    value={editor.state.bodyType}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Age"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: 24</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput fieldName="age" parentClass="w-full" value={editor.state.age} />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Butt"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Medium</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput fieldName="butt" parentClass="w-full" value={editor.state.butt} />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Hair"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Long, Black</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput fieldName="hair" parentClass="w-full" value={editor.state.hair} />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Breasts"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Huge, DD</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="breasts"
                    parentClass="w-full"
                    value={editor.state.breasts}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Ethnicity"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: American</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="ethnicity"
                    parentClass="w-full"
                    value={editor.state.ethnicity}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Country"
                  helperText={
                    <div class="flex flex-col">
                      <span>Two letter country code. Examples: US, AR, ES, MX</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="country"
                    parentClass="w-full"
                    value={editor.state.country}
                  />
                </div>
              </Card>
            </div>

            <div class={`flex flex-col gap-2`} classList={{ hidden: tabs.current() !== 'Gallery' }}>
              <Show when={modelGalleryImagesRemaining() > 0}>
                <div class="mt-2 text-xs">
                  Uploading {modelGalleryImagesRemaining()} images to the model's gallery...
                </div>
              </Show>
              <input
                class="mt-2"
                type="file"
                multiple
                use:fileUploader={{
                  userCallback: uploadModelGalleryImages,
                  setFiles,
                }}
              />
              <div class="my-3 grid grid-cols-6 gap-3">
                {modelGalleryImages().map((image) => {
                  return (
                    <div class="group relative ">
                      <div
                        class="absolute -right-1 -top-1 hidden h-4 w-4 cursor-pointer rounded-full bg-white group-hover:block"
                        onClick={async () => {
                          if (confirm('Are you sure you want to remove this image?')) {
                            await removeModelGalleryImage(image._id)
                            toastStore.success('Image removed from model gallery')
                            await loadModelGallery()
                          }
                        }}
                      >
                        <X aria-hidden="true" class="absolute h-4 w-4 text-black" />
                      </div>
                      <img class="" src={getAssetUrl(image.url)} />
                    </div>
                  )
                })}
              </div>
            </div>

            <Show when={!props.close}>
              <div class="flex w-full justify-end gap-2">{footer}</div>
            </Show>
          </div>
        </div>
      </form>
      <Show when={showBuilder()}>
        <SpriteModal
          body={editor.state.sprite}
          onChange={(body) => {
            editor.update('sprite', body)
            setShowBuilder(false)
          }}
          show={showBuilder()}
          close={() => setShowBuilder(false)}
        />
      </Show>

      <Show when={converted()}>
        <DownloadModal
          show
          close={() => setConverted(undefined)}
          char={converted()!}
          charId={converted()!._id}
        />
      </Show>
      <ImportCharacterModal
        show={showImport()}
        close={() => setImport(false)}
        onSave={(char, imgs) => {
          editor.load(char[0])
          editor.receiveAvatar(imgs[0]!)
          setImage(imgs[0] as any)
          setImport(false)
        }}
        single
      />

      <AvatarModal url={imgUrl()} close={() => setImageUrl('')} />

      <Show when={openPreset()}>
        <RootModal
          title="Update Preset"
          show
          close={() => setOpenPreset(false)}
          maxWidth="half"
          maxHeight
          footer={presetFooter()}
        >
          <sub>This preset used for character generation</sub>
          <ModeGenSettings
            presetId={user.user?.chargenPreset || user.user?.defaultPreset}
            onPresetChanged={(id) => userStore.updatePartialConfig({ chargenPreset: id })}
            close={() => setOpenPreset(false)}
            hideTabs={['Memory', 'Prompt']}
            footer={setPresetFooter}
          />
        </RootModal>
      </Show>
    </Page>
  )
}

const Regenerate: Component<{
  field: string
  trait?: string
  editor: CharEditor
  allowed: boolean
}> = (props) => {
  return (
    <Tooltip tip="Name and description must be filled" disable={props.editor.canGenerate()}>
      <Switch>
        <Match when={!props.allowed}>{null}</Match>

        <Match when={props.allowed}>
          <Button
            size="sm"
            class="inline-block"
            onClick={() => {
              if (!props.editor.canGenerate()) {
                toastStore.warn(`Fill in the Name and Description to generate`)
                return
              }
              props.editor.generateField(props.field, props.trait)
            }}
            disabled={props.editor.generating()}
          >
            <WandSparkles size={16} />
          </Button>
        </Match>
      </Switch>
    </Tooltip>
  )
}

const AvatarModal: Component<{ url?: string; close: () => void }> = (props) => {
  rootModalStore.addModal({
    id: 'char-avatar-modal',
    element: (
      <Modal show={!!props.url} close={props.close} maxWidth="half" fixedHeight>
        <div class="flex justify-center p-4">
          <img class="rounded-md" src={getAssetUrl(props.url!)} />
        </div>
      </Modal>
    ),
  })

  return null
}

const AlternateGreetingsInput: Component<{
  greetings: string[]
  setGreetings: (next: string[]) => void
}> = (props) => {
  const addGreeting = () => props.setGreetings([...props.greetings, ''])
  const removeGreeting = (i: number) => {
    return props.setGreetings(props.greetings.slice(0, i).concat(props.greetings.slice(i + 1)))
  }

  const onChange = (ev: { currentTarget: HTMLInputElement | HTMLTextAreaElement }, i: number) => {
    props.setGreetings(
      props.greetings.map((orig, j) => (j === i ? ev.currentTarget?.value ?? '' : orig))
    )
  }

  return (
    <>
      <For each={props.greetings}>
        {(altGreeting, i) => (
          <div class="flex gap-2">
            <TextInput
              isMultiline
              fieldName={`alternateGreeting${i() + 1}`}
              placeholder="An alternate greeting for your character"
              value={altGreeting}
              onChange={(ev) => onChange(ev, i())}
              parentClass="w-full"
            />
            <div class="1/12 flex items-center" onClick={() => removeGreeting(i())}>
              <MinusCircle size={16} class="focusable-icon-button" />
            </div>
          </div>
        )}
      </For>
      <div>
        <Button onClick={addGreeting}>
          <Plus size={16} />
          Add Alternate Greeting
        </Button>
      </div>
    </>
  )
}

const SpriteModal: Component<{
  body?: FullSprite
  onChange: (body: FullSprite) => void
  show: boolean
  close: () => void
}> = (props) => {
  let ref: any

  const [original, setOriginal] = createSignal(props.body)
  const [body, setBody] = createSignal(props.body || getRandomBody())

  createEffect(() => {
    if (props.body && !original()) {
      setOriginal(props.body)
    }
  })

  const handleChange = () => {
    props.onChange(body())
  }

  useRootModal({
    id: 'sprite-modal',
    element: (
      <Modal
        show={props.show}
        close={props.close}
        fixedHeight
        maxWidth="half"
        footer={
          <>
            <Button onClick={() => props.onChange(original()!)} schema="secondary">
              Cancel
            </Button>
            <Button onClick={handleChange}>Confirm</Button>
          </>
        }
      >
        <PageHeader title="Character Designer" />
        <div class="h-[28rem] w-full text-sm sm:h-[42rem]" ref={ref}>
          <AvatarBuilder body={body()} onChange={(body) => setBody(body)} bounds={ref} noHeader />
        </div>
      </Modal>
    ),
  })

  return null
}

const MemoryBookPicker: Component<{
  bundledBook: AppSchema.MemoryBook | undefined
  setBundledBook: (newVal: AppSchema.MemoryBook | undefined) => void
}> = (props) => {
  const memory = memoryStore()
  const [isModalShown, setIsModalShown] = createSignal(false)
  const [entrySort, setEntrySort] = createSignal<EntrySort>('creationDate')
  const updateEntrySort = (item: Option<string>) => {
    if (item.value === 'creationDate' || item.value === 'alpha') {
      setEntrySort(item.value)
    }
  }

  const NONE_VALUE = '__none_character_book__'
  const internalMemoryBookOptions = createMemo(() => [
    { label: 'Import Memory Book', value: NONE_VALUE },
    ...memory.books.list.map((book) => ({ label: book.name, value: book._id })),
  ])
  const pickInternalMemoryBook = (option: Option) => {
    const newBook = memory.books.list.find((book) => book._id === option.value)
    props.setBundledBook(newBook ? { ...newBook, _id: BUNDLED_CHARACTER_BOOK_ID } : undefined)
  }
  const initBlankCharacterBook = () => {
    props.setBundledBook(emptyBookWithEmptyEntry())
  }
  const deleteBook = () => {
    props.setBundledBook(undefined)
  }
  const ModalFooter = () => (
    <>
      <Button schema="secondary" onClick={() => setIsModalShown(false)}>
        Close
      </Button>
      <Button type="submit">
        <Save />
        Save Character Book
      </Button>
    </>
  )
  const onSubmitCharacterBookChanges = (ev: Event) => {
    ev.preventDefault()
    const update = getBookUpdate(ev)
    if (props.bundledBook) {
      props.setBundledBook({ ...props.bundledBook, ...update })
    }
    setIsModalShown(false)
  }

  const BookModal = (
    <Modal
      title="Chat Memory"
      show={isModalShown()}
      close={() => setIsModalShown(false)}
      footer={<ModalFooter />}
      onSubmit={onSubmitCharacterBookChanges}
      maxWidth="half"
      fixedHeight
    >
      <div class="text-sm">
        <EditMemoryForm
          hideSave
          book={props.bundledBook!}
          entrySort={entrySort()}
          updateEntrySort={updateEntrySort}
        />
      </div>
    </Modal>
  )

  useRootModal({ id: 'memoryBook', element: BookModal })

  return (
    <div>
      <h4 class="flex gap-1 text-lg">
        <div>Character Book</div>
        <Button size="sm" onClick={initBlankCharacterBook}>
          Create New Book
        </Button>
      </h4>
      <Show when={!props.bundledBook}>
        <span class="text-sm"> This character doesn't have a Character Book. </span>
        <div class="flex flex-col gap-3 sm:flex-row">
          <Select
            fieldName="memoryBook"
            value={NONE_VALUE}
            items={internalMemoryBookOptions()}
            onChange={pickInternalMemoryBook}
          />
        </div>
      </Show>
      <Show when={props.bundledBook}>
        <span class="text-sm">This character has a Character Book.</span>
        <div class="mt-2 flex gap-3">
          <Button onClick={() => setIsModalShown(true)}>Edit Book</Button>
          <Button onClick={deleteBook}>Delete Book</Button>
        </div>
      </Show>
    </div>
  )
}

const AdvancedOptions: Component<{ editor: CharEditor }> = (props) => {
  return (
    <>
      <Card class="flex flex-col gap-2">
        <CharacterSchema
          characterId={props.editor.state.editId}
          inherit={props.editor.state.json}
          update={(next) => props.editor.update('json', next)}
        />
        <TextInput
          isMultiline
          fieldName="systemPrompt"
          label="Character System Prompt (optional)"
          helperText={
            <span>
              {`System prompt to bundle with your character. You can use the {{original}} placeholder to include the user's own system prompt, if you want to supplement it instead of replacing it.`}
            </span>
          }
          placeholder="Enter roleplay mode. You will write {{char}}'s next reply in a dialogue between {{char}} and {{user}}. Do not decide what {{user}} says or does. Use Internet roleplay style, e.g. no quotation marks, and write user actions in italic in third person like: *example*. You are allowed to use markdown. Be proactive, creative, drive the plot and conversation forward. Write at least one paragraph, up to four. Always stay in character. Always keep the conversation going. (Repetition is highly discouraged)"
          value={props.editor.state.systemPrompt}
        />
        <TextInput
          isMultiline
          fieldName="postHistoryInstructions"
          label="Character Jailbreak (optional)"
          helperText={
            <span>
              {`Prompt to bundle with your character, used at the bottom of the prompt. You can use the {{original}} placeholder to include the user's jailbreak (UJB), if you want to supplement it instead of replacing it.`}
            </span>
          }
          placeholder="Write at least four paragraphs."
          value={props.editor.state.postHistoryInstructions}
        />
        <TextInput
          isMultiline
          class="min-h-[80px]"
          fieldName="insertPrompt"
          label="Insert / Depth Prompt"
          helperMarkdown={`A.k.a. Author's note. Prompt to be placed near the bottom of the chat history, **Insert Depth** messages from the bottom.`}
          placeholder={`E.g. ### Instruction: Write like James Joyce.`}
          value={props.editor.state.insert?.prompt}
        />
        <RangeInput
          fieldName="insertDepth"
          label="Insert Depth"
          helperText={
            <>
              The number of messages that should exist below the <b>Insert Prompt</b>. Between 1 and
              5 is recommended.
            </>
          }
          min={0}
          max={10}
          step={1}
          value={props.editor.state.insert?.depth ?? 3}
        />
      </Card>
      <Card>
        <MemoryBookPicker
          setBundledBook={(book) => props.editor.update('book', book)}
          bundledBook={props.editor.state.book}
        />
      </Card>
      <Card>
        <TextInput
          fieldName="creator"
          label="Creator (optional)"
          placeholder="e.g. John1990"
          value={props.editor.state.creator}
        />
      </Card>
      <Card>
        <TextInput
          fieldName="characterVersion"
          label="Character Version (optional)"
          placeholder="any text e.g. 1, 2, v1, v1fempov..."
          value={props.editor.state.characterVersion}
        />
      </Card>
    </>
  )
}

const ReelControl: Component<{ editor: CharEditor; loading: boolean }> = (props) => {
  const createAvatar = async () => {
    const base64 = await props.editor.createAvatar()
    if (!base64) return

    await props.editor.imageCache.addImage(base64, `${v4()}.png`)
  }

  const size = 14

  return (
    <div class="flex flex-col items-center gap-1">
      <div class="flex w-fit gap-2">
        <Button
          size="sm"
          disabled={props.editor.imageCache.state.images.length <= 1 || props.loading}
          onClick={props.editor.imageCache.prev}
        >
          <ArrowLeft size={size} />
        </Button>

        <Button
          size="sm"
          disabled={props.editor.imageCache.state.imageId === '' || props.loading}
          onClick={() => props.editor.imageCache.removeImage(props.editor.imageCache.state.imageId)}
        >
          <Trash size={size} />
        </Button>

        <Button
          size="sm"
          disabled={props.editor.imageCache.state.images.length <= 1 || props.loading}
          onClick={props.editor.imageCache.next}
        >
          <ArrowRight size={size} />
        </Button>
      </div>
      <div class="flex w-fit gap-2">
        {/* <Button size="sm" >
          <RotateCcw size={size} />
        </Button> */}
        <Button size="sm" onClick={createAvatar} disabled={props.loading}>
          <WandSparkles size={size} />
        </Button>
      </div>
    </div>
  )
}
